import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CloudUpload, Search, X } from 'lucide-react';
import UserStatsSummary from '@/components/UserStatsSummary';
import { useWelcomeTour } from '@/hooks/useWelcomeTour';
import { NetworkStrengthIndicator } from '@/components/NetworkStrengthIndicator';
import UserSearchResults from '@/components/UserSearchResults';
import { BACKEND_URL } from '@/lib/constants';

interface User {
  username: string;
  name?: string;
}

const LandingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { startTour } = useWelcomeTour();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<User[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  useEffect(() => {
    const isCompleted = localStorage.getItem('welcomeTourCompleted');
    if (isCompleted !== 'true') {
      const timer = setTimeout(() => {
        startTour();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [startTour]);

  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setUserSearchResults([]);
      setIsSearching(false);
      setSearchError(null);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/users/search?query=${encodeURIComponent(query)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Search failed');
      }

      const users = await response.json();
      setUserSearchResults(users);
    } catch (err) {
      const error = err as Error;
      setSearchError(error.message);
      setUserSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSelectUser = useCallback(
    (username: string) => {
      setSearchQuery('');
      setUserSearchResults([]);
      setSearchError(null);
      navigate(`/profile/${encodeURIComponent(username)}`);
    },
    [navigate],
  );

  const handleCloseSearch = useCallback(() => {
    setSearchQuery('');
    setUserSearchResults([]);
    setSearchError(null);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 px-4 relative overflow-hidden">
      {/* Decorative background elements matching login page */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-emerald-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-blue-200/20 rounded-full blur-3xl"></div>

      {/* Top bar: network indicator pinned to top-right, never overlaps stats */}
      <div className="flex justify-end pt-[calc(var(--app-safe-area-top)+0.75rem)] pr-2">
        <NetworkStrengthIndicator />
      </div>

      <div className="max-w-4xl w-full mx-auto pb-8">
        {/* Stats Summary at the top */}
        <div id="tour-stats-summary">
          <UserStatsSummary />
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-8 relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder={t(
                'common.searchUsersPlaceholder',
                'Search users by name or ID...',
              )}
              value={searchQuery}
              onChange={(e) => {
                const value = e.target.value;
                setSearchQuery(value);
                if (debounceTimerRef.current) {
                  clearTimeout(debounceTimerRef.current);
                }
                if (!value.trim()) {
                  setUserSearchResults([]);
                  setIsSearching(false);
                  setSearchError(null);
                  return;
                }
                debounceTimerRef.current = setTimeout(() => {
                  handleSearch(value);
                }, 400);
              }}
              className="w-full pl-10 pr-10 py-3 border border-slate-300 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
            />
            {searchQuery && (
              <button
                onClick={handleCloseSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* User Search Results */}
        {(isSearching || userSearchResults.length > 0 || searchError) && (
          <UserSearchResults
            users={userSearchResults}
            onSelectUser={handleSelectUser}
            isLoading={isSearching}
            error={searchError || undefined}
            isVisible={true}
            onClose={handleCloseSearch}
          />
        )}

        <div className="text-center space-y-20">
          {/* Swecha Logo */}
          <div className="animate-fade-in-smooth" id="tour-swecha-logo">
            <img
              src="/Swecha_Logo_English.png"
              alt={t('common.swechaTechnologyForSociety')}
              className="h-28 mx-auto drop-shadow-lg hover:scale-105 transition-transform duration-300"
            />
          </div>

          {/* Circular Upload Button */}
          <div className="animate-fade-in-delay-smooth flex justify-center">
            <button
              id="tour-upload-btn"
              onClick={() => navigate('/upload')}
              className="group relative w-80 h-80 rounded-full bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-600 shadow-2xl transform transition-all duration-500 hover:scale-105 hover:shadow-emerald-500/50 overflow-hidden"
            >
              <div className="absolute -inset-2 bg-gradient-to-br from-emerald-300 to-emerald-500 rounded-full opacity-20 blur-xl group-hover:opacity-40 transition-opacity duration-500"></div>
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="relative z-10 flex flex-col items-center justify-center h-full gap-8">
                <div className="relative">
                  <div className="absolute inset-0 bg-white/20 rounded-full animate-ping-slow"></div>
                  <div
                    className="absolute inset-0 bg-white/10 rounded-full"
                    style={{
                      animation:
                        'ping-slow 4s cubic-bezier(0, 0, 0.2, 1) infinite',
                    }}
                  ></div>
                  <div className="relative bg-white/25 backdrop-blur-md rounded-full p-6 group-hover:bg-white/35 transition-all duration-500 shadow-lg">
                    <CloudUpload
                      className="w-24 h-24 text-white animate-upload-float drop-shadow-lg"
                      strokeWidth={2.5}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <h2 className="text-3xl font-bold text-white drop-shadow-md">
                    {t('common.uploadFiles')}
                  </h2>
                  <p className="text-white/80 text-sm font-medium">
                    {t('common.click.to.get.started')}
                  </p>
                </div>
              </div>

              <div className="absolute inset-0 rounded-full border-4 border-white/20 group-hover:border-white/40 transition-all duration-500"></div>
            </button>
          </div>

          <p className="text-slate-500 text-sm animate-fade-in-late">
            {t('common.contributeToOpenDataAndHelpBuildTheFuture')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
